// src/pages/approval/ExpenseResolutionNew.jsx
// ✅ 지출결의서 작성 — 지출기안의 후속 문서(WEHAGO 지출결의서 양식 기반).
// DraftDocumentNew 래퍼가 아니라 독립 컴포넌트 — 공문형 레이아웃(수신/경유/붙임)을
// 쓰지 않고, 필드-값 2열 테이블 양식을 별도로 그린다.
import React, { useEffect, useState } from "react";
import useBackdropClose from "../../hooks/useBackdropClose";
import api from "../../api";
import ApprovalLine from "../../components/approval-line/ApprovalLine";
import ApprovalLineModal from "../../components/approval-line/ApprovalLineModal";
import ApprovalLineConfirmDialog from "../../components/approval-line/ApprovalLineConfirmDialog";
import ExpenseResolutionPreview from "../../components/preview/expense/ExpenseResolutionPreview";
import {
  CATEGORY_LABEL,
  PAY_METHOD_LABEL,
  toISODate,
  extractDateSeq,
  formatBudgetItem,
  countExistingResolutions,
  computeResolutionDraftNo,
  drafterAffiliationOf,
} from "../../utils/resolutionDraft";

// ✅ 지출기안(expense_draft)에 실제 존재하는 카테고리만 — CATEGORY_LABEL은 수입까지
// 포함한 전체 합집합이라 필터 목록에는 이 배열로 범위를 제한한다.
const EXPENSE_DRAFT_CATEGORIES = Object.keys(CATEGORY_LABEL);

export default function ExpenseResolutionNew() {
  const loadBackdrop = useBackdropClose(() => setShowLoadModal(false));

  useEffect(() => {
    document.title = "지출결의 작성";
  }, []);

  // ✅ 불러온 지출기안(원본)에서 상속되는 값들 — 다음 커밋("불러오기 연동")에서 실제로 채워진다
  const [sourceDraft, setSourceDraft] = useState(null);
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [partnerName, setPartnerName] = useState("");
  const [payMethodLabel, setPayMethodLabel] = useState("");
  const [budgetItemFormatted, setBudgetItemFormatted] = useState("");
  const [internalDraftNo, setInternalDraftNo] = useState("");
  const [documentTitle, setDocumentTitle] = useState("");

  // ✅ 이 페이지에서 직접 입력하는 값들
  const [expenseDate, setExpenseDate] = useState(() => toISODate(new Date()));
  const [accountInfo, setAccountInfo] = useState("");
  const [attachmentsText, setAttachmentsText] = useState(
    "내부기안,계좌이체확인증,세금계산서,명세서"
  );

  // ✅ 작성일자(자동) — 페이지가 열린 시점 기준. 저장 시각과 동일하게 유지.
  const [draftDate] = useState(() => toISODate(new Date()));

  // ✅ 기안자 표시 — 로그인 사용자 정보로 자동 구성
  const [drafterLine1, setDrafterLine1] = useState("");
  const [drafterLine2, setDrafterLine2] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/me/");
        setDrafterLine1(`${data.name || ""} ${data.position || ""}`.trim());
        // ✅ 매핑(utils/resolutionDraft.js의 DRAFTER_TEAM_BY_NAME)에 없는 이름(예:
        // 대표이사)이면 drafterAffiliationOf가 빈 문자열을 반환해 2번째 줄 자체가 생략됨.
        setDrafterLine2(drafterAffiliationOf(data.name));
      } catch (err) {
        console.error("❌ 로그인 사용자 정보 조회 실패:", err);
      }
    })();
  }, []);

  const [approvers, setApprovers] = useState([]);
  const [showApproverModal, setShowApproverModal] = useState(false);

  const categoryLabel = CATEGORY_LABEL[category] || category;

  // ✅ 기안 불러오기 — 원천은 지출기안(document_type=expense_draft) 문서뿐. 지출 원시
  // 데이터(BusinessExpense 등)는 직접 불러오지 않고 항상 지출기안을 경유한다.
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [loadCategoryFilter, setLoadCategoryFilter] = useState("");
  const [expenseDraftList, setExpenseDraftList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const fetchExpenseDraftList = async (categoryValue) => {
    setLoadingList(true);
    try {
      const params = { document_type: "expense_draft" };
      if (categoryValue) params.category = categoryValue;
      const { data } = await api.get("/draft/documents/", { params });
      setExpenseDraftList(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("❌ 지출기안 목록 조회 실패:", err);
      setExpenseDraftList([]);
    } finally {
      setLoadingList(false);
    }
  };

  const openLoadModal = () => {
    setShowLoadModal(true);
    fetchExpenseDraftList(loadCategoryFilter);
  };

  const handleCategoryFilterChange = (value) => {
    setLoadCategoryFilter(value);
    fetchExpenseDraftList(value);
  };

  const handleSelectDraft = async (item) => {
    const existingCount = await countExistingResolutions(api, "expense_resolution", item.id);
    if (existingCount > 0) {
      const proceed = window.confirm(
        `이 지출기안(${item.draft_no})으로 작성된 지출결의서가 이미 ${existingCount}건 있습니다.\n계속 진행하시겠습니까?`
      );
      if (!proceed) return;
    }

    try {
      const { data: detail } = await api.get(`/draft/documents/${item.id}/`);

      setSourceDraft(detail);
      setCategory(detail.category);
      setAmount(String(detail.amount || ""));
      setPartnerName(detail.partner_name || "");
      setPayMethodLabel(PAY_METHOD_LABEL[detail.pay_method] || detail.pay_method || "");
      setInternalDraftNo(detail.draft_no || "");
      setBudgetItemFormatted(formatBudgetItem(detail.template_data?.budgetItem));

      const label = CATEGORY_LABEL[detail.category] || detail.category;
      setDocumentTitle(`[${label}] ${detail.title}-${extractDateSeq(detail.draft_no)}`);

      // ✅ 계좌정보 자동 — 거래처가 실제 Vendor에 연결돼 있고 은행/계좌 정보가 있을 때만.
      // 없으면 수동 입력(빈 값)으로 남겨둔다.
      if (detail.partner) {
        try {
          const { data: vendor } = await api.get(`/vendors/${detail.partner}/`);
          if (vendor.bank_name && vendor.account_number) {
            setAccountInfo(`${detail.partner_name}_${vendor.bank_name}_${vendor.account_number}`);
          } else {
            setAccountInfo("");
          }
        } catch (err) {
          console.error("❌ 거래처 계좌정보 조회 실패:", err);
          setAccountInfo("");
        }
      } else {
        setAccountInfo("");
      }

      setShowLoadModal(false);
    } catch (err) {
      console.error("❌ 지출기안 상세 조회 실패:", err);
      alert("지출기안을 불러오지 못했습니다.");
    }
  };

  // ✅ 저장 — 지출결의는 자체 채번을 하지 않고 불러온 지출기안의 draft_no를 그대로
  // 화면에 노출한다(내부기안번호). 다만 DraftDocument.draft_no는 unique 제약이 있어
  // 원본과 완전히 같은 문자열을 그대로 저장하면 충돌한다(같은 기안으로 결의서를 여러
  // 건 만들 때는 더더욱) — 그래서 모델의 draft_no 필드에만 "-R{순번}" 접미를 붙여
  // 유니크를 확보하고, 화면에 보이는 모든 값(내부기안번호/문서제목 등)은 원본 그대로
  // 쓴다. 목록/상세 화면도 이 접미가 새지 않도록 template_data.internalDraftNo를
  // 우선 표시하도록 별도 수정함(DraftDocumentList.jsx/DraftDocumentDetail.jsx).
  const [saving, setSaving] = useState(false);
  // ✅ 상신 직전 결재라인 재확인 다이얼로그 — 취소 시 작성 화면 유지(저장 안 함),
  // 상신 클릭 시에만 doSubmit()이 실제로 실행된다.
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);

  const onSubmit = () => {
    if (!sourceDraft) {
      alert("먼저 '기안 불러오기'로 지출기안을 선택해주세요.");
      return;
    }
    if (!expenseDate) {
      alert("지출일을 입력해주세요.");
      return;
    }
    if (approvers.length === 0) {
      alert("결재선을 등록해주세요.\n('수정' 버튼을 눌러 결재자를 추가하세요.)");
      return;
    }
    setShowApprovalConfirm(true);
  };

  const doSubmit = async () => {
    setShowApprovalConfirm(false);
    setSaving(true);
    try {
      const existingCount = await countExistingResolutions(api, "expense_resolution", sourceDraft.id);
      const suffix = existingCount + 1;
      const resolutionDraftNo = computeResolutionDraftNo(sourceDraft.draft_no, suffix);

      const approval_lines_data = approvers.map((approver, idx) => ({
        approver_id: approver.id,
        approval_type: approver.status || "결재",
        order: idx + 1,
        position: approver.position || "",
      }));

      const payload = {
        document_type: "expense_resolution",
        draft_no: resolutionDraftNo,
        seq: String(suffix).padStart(3, "0"),
        title: documentTitle,
        category,
        subcategory: sourceDraft.subcategory,
        detail_option: sourceDraft.detail_option || "",
        draft_date: draftDate,
        claim_date: null,
        work_month: sourceDraft.work_month,
        partner: sourceDraft.partner || null,
        partner_name: partnerName,
        amount: amount ? Number(amount) : 0,
        pay_method: sourceDraft.pay_method,
        desc: "",
        attachment_items: [],
        template_code: "",
        template_data: {
          internalDraftNo,
          accountInfo,
          budgetItemFormatted,
          attachmentsText,
          drafterLine1,
          drafterLine2,
          expenseDate,
          categoryLabel,
        },
        source_app: "draft_document",
        source_id: sourceDraft.id,
        approval_lines_data,
      };

      const { data } = await api.post("/draft/documents/", payload);
      await api.post(`/draft/documents/${data.id}/submit/`);

      alert(`✅ 저장되었습니다.\n내부기안번호: ${internalDraftNo}`);
    } catch (err) {
      console.error("❌ 지출결의서 저장 실패:", err);
      alert(err?.response?.data ? JSON.stringify(err.response.data) : "저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24, display: "flex", gap: 24, alignItems: "flex-start" }}>
      <div style={{ width: 360, flexShrink: 0 }}>
        <h2 style={{ marginBottom: 20 }}>📝 지출결의서 작성</h2>

        <button
          type="button"
          style={{ width: "100%", height: 40, marginBottom: 20 }}
          onClick={openLoadModal}
        >
          📥 기안 불러오기
        </button>

        {internalDraftNo && (
          <div style={{ marginBottom: 16, fontSize: 13, color: "#555", whiteSpace: "nowrap" }}>
            불러온 기안: {internalDraftNo}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 4, whiteSpace: "nowrap" }}>
            지출일
          </label>
          <input
            type="date"
            value={expenseDate}
            onChange={(e) => setExpenseDate(e.target.value)}
            style={{ width: "100%", height: 32 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 4, whiteSpace: "nowrap" }}>
            계좌정보
          </label>
          <input
            type="text"
            value={accountInfo}
            onChange={(e) => setAccountInfo(e.target.value)}
            placeholder="거래처_은행_계좌번호"
            style={{ width: "100%", height: 32 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 4, whiteSpace: "nowrap" }}>
            첨부
          </label>
          <input
            type="text"
            value={attachmentsText}
            onChange={(e) => setAttachmentsText(e.target.value)}
            style={{ width: "100%", height: 32 }}
          />
        </div>

        <ApprovalLine
          approvers={approvers}
          setApprovers={setApprovers}
          onOpenModal={() => setShowApproverModal(true)}
          documentType="expense_resolution"
        />
      </div>

      <div style={{ flex: 1 }}>
        <ExpenseResolutionPreview
          categoryLabel={categoryLabel}
          documentTitle={documentTitle}
          draftDate={draftDate}
          drafterLine1={drafterLine1}
          drafterLine2={drafterLine2}
          expenseDate={expenseDate}
          partnerName={partnerName}
          amount={amount}
          payMethodLabel={payMethodLabel}
          accountInfo={accountInfo}
          budgetItemFormatted={budgetItemFormatted}
          internalDraftNo={internalDraftNo}
          attachmentsText={attachmentsText}
          approvalLine={approvers}
          onSave={onSubmit}
          saving={saving}
        />
      </div>

      {showApproverModal && (
        <ApprovalLineModal
          onClose={() => setShowApproverModal(false)}
          onSelect={setApprovers}
          documentType="expense_resolution"
          initialApprovers={approvers}
        />
      )}

      {showApprovalConfirm && (
        <ApprovalLineConfirmDialog
          approvers={approvers}
          onCancel={() => setShowApprovalConfirm(false)}
          onConfirm={doSubmit}
        />
      )}

      {showLoadModal && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
          }}
          {...loadBackdrop}
        >
          <div
            style={{ background: "#fff", borderRadius: 8, padding: 20, width: 640, maxHeight: "70vh", overflowY: "auto" }}
          >
            <h3 style={{ marginTop: 0, whiteSpace: "nowrap" }}>📥 지출기안 불러오기</h3>

            <select
              value={loadCategoryFilter}
              onChange={(e) => handleCategoryFilterChange(e.target.value)}
              style={{ height: 32, marginBottom: 12 }}
            >
              <option value="">전체 카테고리</option>
              {EXPENSE_DRAFT_CATEGORIES.map((key) => (
                <option key={key} value={key}>{CATEGORY_LABEL[key]}</option>
              ))}
            </select>

            {loadingList ? (
              <div style={{ padding: 24, textAlign: "center", color: "#aaa" }}>불러오는 중...</div>
            ) : expenseDraftList.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "#aaa" }}>지출기안이 없습니다.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid #ddd" }}>기안번호</th>
                    <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid #ddd" }}>제목</th>
                    <th style={{ textAlign: "right", padding: "6px 4px", borderBottom: "1px solid #ddd" }}>금액</th>
                    <th style={{ padding: "6px 4px", borderBottom: "1px solid #ddd" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {expenseDraftList.map((item) => (
                    <tr key={item.id}>
                      <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>{item.draft_no}</td>
                      <td style={{ padding: "6px 4px" }}>{item.title}</td>
                      <td style={{ padding: "6px 4px", textAlign: "right" }}>{Number(item.amount || 0).toLocaleString()}원</td>
                      <td style={{ padding: "6px 4px" }}>
                        <button
                          type="button"
                          onClick={() => handleSelectDraft(item)}
                          style={{ width: "56px", height: "28px" }}
                        >
                          선택
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
